/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  Stack,
  Typography,
  Alert,
  AlertTitle,
  Button,
  DialogContent,
} from '@mui/material';
import { Message, MessageType } from '@marageti/z4-sdk/lib/travel';
import { Controller, useForm, SubmitHandler } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { LoadingButton } from '@mui/lab';
import { getSubmitButtonProps, RichTextEditor } from '@marageti/z4-lib';
import useZicassoChat from '../../hooks/use-zicasso-chat';
import useUser from '../../hooks/use-user';

const schema = z.object({
  message: z.string().min(1, { message: 'Message is required' }),
});

type LogInternalNoteFormData = z.infer<typeof schema>;

const LogInternalNoteModal = ({
  open,
  onClose,
  conversationId,
  subscribeToRealtime,
}: {
  open: boolean;
  onClose: () => void;
  conversationId: string;
  subscribeToRealtime?: boolean;
}) => {
  const user = useUser();
  const { enqueueSnackbar } = useSnackbar();
  const [error, setError] = useState<Error | null>(null);

  const {
    sendMessage,
    updateMessages,
  } = useZicassoChat({
    conversationId,
    subscribeToRealtime: subscribeToRealtime !== false,
  });

  const {
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    control,
    reset: resetForm,
  } = useForm<LogInternalNoteFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      message: '',
    },
  });

  const reset = () => {
    setError(null);
    resetForm();
  };

  const handleClose = () => {
    onClose();
    reset();
  };

  const onSubmit: SubmitHandler<LogInternalNoteFormData> = async (data) => {
    const { message } = data;

    const senderName = user.data?.fullName;
    const title = `Note from ${senderName}`;

    const messageToSend: Partial<Message> = {
      conversationId,
      text: message,
      title,
      messageType: MessageType.ZicassoNote,
    };

    try {
      await sendMessage(messageToSend);
      await updateMessages();
      enqueueSnackbar('Internal note created.', {
        variant: 'success',
      });
      handleClose();
    } catch (e) {
      setError(e as Error);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ px: 3 }}>Log Internal Note</DialogTitle>
      <DialogContent sx={{
        p: 0,
        m: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
      >
        {error && (
        <Alert severity="error">
          <AlertTitle>There was an error submitting note</AlertTitle>
          {error?.message}
        </Alert>
        )}
        <Stack
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            overflow: 'hidden',
          }}
        >
          <Stack
            spacing={1.5}
            sx={{
              px: 3,
              pt: 1,
              pb: 2.5,
              flex: 1,
              overflowY: 'auto',
            }}
          >

            <Controller
              control={control}
              name="message"
              rules={{ maxLength: 6000 }}
              render={({ field }) => (
                <RichTextEditor
                  editorName="Message"
                  error={!!errors.message}
                  helperText={errors.message?.message as string}
                  label="Message"
                  onChange={field.onChange}
                  useLinks
                  useLists
                  sx={{
                    height: { mobile: '400px', desktop: '400px' },
                    minHeight: '200px',
                    flex: 1,
                  }}
                />
              )}
            />
            <Typography variant="caption" color="text.secondary">
              Notes are internal only, not visible to the other party.
            </Typography>
          </Stack>
          <Stack
            direction="row"
            spacing={2}
            sx={(theme) => ({
              borderTop: `1px solid ${theme.palette.divider}`,
              py: 2.5,
              px: 3,
              flexShrink: 0,
            })}
          >
            <LoadingButton
              type="submit"
              variant="contained"
              sx={{
                width: { desktop: 'fit-content' },
              }}
              {...getSubmitButtonProps(isSubmitting || !open, isValid)}
            >
              Submit
            </LoadingButton>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{
                width: { desktop: 'fit-content' },
              }}
            >
              Cancel
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default LogInternalNoteModal;
